import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const KitchenMenu = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [recipe, setRecipe] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchMenuItems(), fetchCategories(), fetchInventory()]);
    setLoading(false);
  };

  const fetchMenuItems = async () => {
    try {
      const data = await apiGet('/admin/kitchen/menu/items');
      if (data.success) setMenuItems(data.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiGet('/admin/pos/setup/categories');
      if (data.success) setCategories(data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const data = await apiGet('/admin/kitchen/inventory/items');
      if (data.success) setInventoryItems(data.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const viewRecipe = async (item) => {
    setSelectedItem(item);
    try {
      const data = await apiGet('/admin/kitchen/menu/items/${item.id}/recipe');
      if (data.success) {
        setRecipe(data.data);
        setShowRecipeModal(true);
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
    }
  };

  const styles = {
    container: {
      padding: isMobile ? '16px 4px' : '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: isMobile ? '16px' : '24px'
    },
    title: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: colors.white,
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)'
    },
    th: {
      background: colors.lightGray,
      padding: isMobile ? '12px 8px' : '16px 20px',
      textAlign: 'left',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '700',
      color: colors.textDark,
      textTransform: 'uppercase',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    td: {
      padding: isMobile ? '12px 8px' : '16px 20px',
      borderBottom: `1px solid ${colors.lightGray}`,
      fontSize: isMobile ? '12px' : '14px'
    },
    button: {
      padding: isMobile ? '6px 10px' : '8px 14px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '11px' : '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div>Loading menu items...</div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Kitchen Menu Management</h1>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
            Manage menu items, recipes, and ingredients
          </p>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Item Name</th>
                {!isMobile && <th style={styles.th}>Category</th>}
                <th style={styles.th}>Price</th>
                {!isMobile && <th style={styles.th}>Prep Time</th>}
                <th style={styles.th}>Ingredients</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {menuItems.map(item => (
                <tr key={item.id}>
                  <td style={styles.td}>
                    <strong>{item.name}</strong>
                    {item.is_veg ? (
                      <span style={{ marginLeft: '6px', color: colors.success, fontSize: '10px' }}>🟢</span>
                    ) : (
                      <span style={{ marginLeft: '6px', color: colors.textLight, fontSize: '10px' }}>🔴</span>
                    )}
                  </td>
                  {!isMobile && <td style={styles.td}>{item.category_name}</td>}
                  <td style={styles.td}>
                    <strong>₹{parseFloat(item.price).toFixed(2)}</strong>
                  </td>
                  {!isMobile && <td style={styles.td}>{item.preparation_time} min</td>}
                  <td style={styles.td}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: colors.lightGray,
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {item.ingredient_count || 0} items
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button
                      onClick={() => viewRecipe(item)}
                      style={{
                        ...styles.button,
                        background: colors.primary,
                        color: colors.white
                      }}
                    >
                      📋 Recipe
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recipe Modal */}
        {showRecipeModal && selectedItem && (
          <div style={styles.modal} onClick={() => setShowRecipeModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
                Recipe: {selectedItem.name}
              </h3>

              {recipe.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
                  No ingredients defined for this item
                </div>
              ) : (
                <div>
                  {recipe.map(ingredient => (
                    <div key={ingredient.id} style={{
                      padding: '12px',
                      background: colors.lightGray,
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '14px' }}>{ingredient.item_name}</strong>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: colors.primary }}>
                          {ingredient.quantity_required} {ingredient.unit}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textLight }}>
                        Current Stock: {ingredient.current_stock} {ingredient.inventory_unit}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => setShowRecipeModal(false)}
                style={{
                  ...styles.button,
                  background: colors.mediumGray,
                  color: colors.textDark,
                  width: '100%',
                  marginTop: '20px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KitchenMenu;
